// src/components/token/ButtonSelector.tsx
import React from "react";
import { FunnelIcon } from "@heroicons/react/24/outline";

interface ButtonSelectorProps {
  /** Array of item labels to render as chips. */
  options: string[];
  /** Which items are currently selected. */
  selectedItems: string[] | string;
  /** Toggling function invoked when user clicks a chip. */
  handleToggle: (item: string) => void;
  /** Whether to display the "Filter" button. */
  filterButton?: boolean;
  /**
   * If true, automatically render an "All" chip at the beginning.
   * Clicking "All" calls handleToggle("All").
   */
  showAll?: boolean;
}

const ButtonSelector: React.FC<ButtonSelectorProps> = ({
  options,
  selectedItems,
  handleToggle,
  filterButton,
  showAll,
}) => {
  return (
    <div className="flex flex-wrap items-center gap-2 max-w-full">
      {/* Optional "Filter" button */}
      {filterButton && (
        <button
          tabIndex={0}
          className="flex items-center gap-2 text-center text-sm font-medium py-2 px-4 rounded-full text-neutral-900 bg-neutral-200"
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              // Example: handleToggle('someFilter')
            }
          }}
        >
          <FunnelIcon className="w-4 h-4" />
          Filter
        </button>
      )}

      {/* Optional "All" chip */}
      {showAll && (
        <button
          key="All"
          tabIndex={0}
          className={`text-sm font-medium py-2 px-4 border border-neutral-300 rounded-full transition-colors
            whitespace-normal break-words
            ${
              (typeof selectedItems === "string" && selectedItems === "All") ||
              (Array.isArray(selectedItems) && selectedItems.length === 0)
                ? "bg-neutral-900 text-white"
                : "bg-transparent text-neutral-900"
            }
          `}
          onClick={() => handleToggle("All")}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              handleToggle("All");
            }
          }}
        >
          All
        </button>
      )}

      {/* Render each option as a chip */}
      {options.map((option) => {
        const isSelected =
          typeof selectedItems === "string"
            ? selectedItems === option
            : selectedItems.includes(option);

        return (
          <button
            key={option}
            tabIndex={0}
            className={`text-sm font-medium py-2 px-4 border border-neutral-300 rounded-full transition-colors 
              whitespace-normal break-words 
              ${
                isSelected
                  ? "bg-neutral-900 text-white"
                  : "bg-transparent text-neutral-900"
              }
            `}
            onClick={() => handleToggle(option)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                handleToggle(option);
              }
            }}
          >
            {option}
          </button>
        );
      })}
    </div>
  );
};

export default ButtonSelector;
